import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ApiDatabase from 'server';
import UseContext from 'hooks/useContext';
import useModal from 'components/modal/useModal';
import BadgeSquare from 'components/badge/badgeSquare';
import Button from 'components/button';
import DatePicker from 'react-datepicker';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Dots,
  Import,
} from 'assets/icons';
import { toast } from 'react-toastify';

const MONTH_NAMES = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

const formatAmount = (value) =>
  `${Number(value || 0).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

const PayslipTempWorker = ({ idUser, idBestt, initialDate, initialYear }) => {
  const { toggle } = useModal();
  const {
    setModalData,
    refreshPayslips,
    modalDataReturn,
  } = UseContext();

  const [selectedDate, setSelectedDate] = useState(() => {
    if (initialDate instanceof Date && !isNaN(initialDate)) return initialDate;
    if (initialYear) return new Date(Number(initialYear), new Date().getMonth(), 1);
    return new Date();
  });

  const [payslips, setPayslips] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isWaiting, setIsWaiting] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [multiDropdownOpen, setMultiDropdownOpen] = useState(false);

  const multiDropdownRef = useRef(null);

  const year = useMemo(() => String(selectedDate.getFullYear()), [selectedDate]);
  const month = useMemo(
    () => String(selectedDate.getMonth() + 1).padStart(2, '0'),
    [selectedDate]
  );

  const monthLabel = useMemo(
    () => `${MONTH_NAMES[Number(month) - 1]} ${year}`,
    [month, year]
  );

  const fetchPayslips = useCallback(() => {
    if (!idBestt) {
      setPayslips([]);
      setTotalAmount(0);
      setIsWaiting(false);
      return;
    }

    setIsWaiting(true);

    ApiDatabase.listPayslipsByWorker(
      { year, month, id_bestt: idBestt },
      (data) => {
        const rows = Array.isArray(data?.payslips) ? data.payslips : [];

        setPayslips(
          rows.sort((a, b) => {
            const ay = Number(a.year || 0);
            const by = Number(b.year || 0);
            const am = Number(a.month || 0);
            const bm = Number(b.month || 0);

            if (by !== ay) return by - ay;
            return bm - am;
          })
        );
        setTotalAmount(Number(data?.totalAmount || 0));
        setIsWaiting(false);
      },
      (err) => {
        if (err?.name !== 'CanceledError') console.log(err);
        setPayslips([]);
        setTotalAmount(0);
        setIsWaiting(false);
      }
    );
  }, [idBestt, year, month]);

  useEffect(() => {
    fetchPayslips();
    setSelectedIds([]);
  }, [fetchPayslips, refreshPayslips, modalDataReturn]);

  useEffect(() => {
    const handler = (e) => {
      if (
        multiDropdownRef.current &&
        !multiDropdownRef.current.contains(e.target)
      ) {
        setMultiDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedPayslips = useMemo(
    () =>
      payslips.filter((p) => selectedIds.includes(p.id_temp_worker_bestt)),
    [payslips, selectedIds]
  );

  const importableSelectedPayslips = useMemo(
    () => selectedPayslips.filter((p) => p.status === 'not_imported'),
    [selectedPayslips]
  );

  const deletableSelectedPayslips = useMemo(
    () => selectedPayslips.filter((p) => p.status === 'imported'),
    [selectedPayslips]
  );

  const allChecked =
    payslips.length > 0 && selectedIds.length === payslips.length;

  const toggleAll = () => {
    if (allChecked) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(payslips.map((p) => p.id_temp_worker_bestt));
  };

  const handleMultipleImport = () => {
    setMultiDropdownOpen(false);
    if (importableSelectedPayslips.length === 0) return;

    setModalData({
      payslips: importableSelectedPayslips,
      year,
      month,
    });
    toggle('payslip_import_global');
  };

  const handleMultipleDelete = () => {
    setMultiDropdownOpen(false);
    if (deletableSelectedPayslips.length === 0) return;

    setModalData({
      payslips: deletableSelectedPayslips,
      year,
      month,
    });
    toggle('payslip_delete');
  };

  const handleExportCSV = async () => {
    const t = localStorage.getItem('xsrfToken');
    const url = `${process.env.REACT_APP_API_URL}admin/exportPayslipsCSV?year=${year}&month=${month}&id_bestt=${idBestt}`;
    try {
      const res = await fetch(url, {
        credentials: 'include',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `export-payslips-${year}-${month}-bestt-${idBestt}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Export impossible');
    }
  };

  return (
    <div className="border rounded p-3 flex flex-col gap-4">
      <div className="flex justify-between gap-2 items-center">
        <div className="font-medium">Fiches de paie</div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2 items-center flex-wrap">
          <div className="flex items-center gap-1 text-sm">
            <button
              className="flex items-center justify-center w-8 h-8 rounded-lg border hover:bg-gray-100"
              onClick={() =>
                setSelectedDate(
                  (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
                )
              }
            >
              <ChevronLeft wh={14} color="#374151" />
            </button>

            <DatePicker
              selected={selectedDate}
              onChange={(date) => {
                if (date) setSelectedDate(date);
              }}
              showMonthYearPicker
              dateFormat="MM/yyyy"
              className="w-full min-w-[110px] h-8 ft-b border rounded-lg text-center text-sm"
              locale="fr-FR"
            />

            <button
              className="flex items-center justify-center w-8 h-8 rounded-lg border hover:bg-gray-100"
              onClick={() =>
                setSelectedDate(
                  (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
                )
              }
            >
              <ChevronRight wh={14} color="#374151" />
            </button>
          </div>

          <div className="border rounded py-1 px-3 text-sm font-medium bg-gray-50">
            {isWaiting ? (
              <span className="inline-block w-14 h-4 bg-gray-200 rounded animate-pulse align-middle" />
            ) : (
              formatAmount(totalAmount)
            )}
          </div>

          <div className="text-sm text-gray-500">
            {monthLabel}
          </div>
        </div>

        <div className="flex gap-2 items-center">
          {selectedIds.length > 0 && (
            <div className="relative" ref={multiDropdownRef}>
              <Button
                onClick={() => setMultiDropdownOpen((prev) => !prev)}
                color="PRIMARY"
                size="SMI"
              >
                <div className="flex gap-1 items-center text-sm">
                  <span>Actions ({selectedIds.length})</span>
                  <ChevronDown wh={16} color="#FFFFFF" />
                </div>
              </Button>

              {multiDropdownOpen && (
                <div className="absolute border-2 border-[#F3F4F6] rounded-lg shadow-lg w-48 top-9 right-0 z-50 bg-gray-50">
                  <div
                    className={`flex flex-row gap-2 items-center h-9 p-2 ft-sm ${
                      importableSelectedPayslips.length > 0
                        ? 'cursor-pointer hover:bg-gray-100'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                    onClick={handleMultipleImport}
                  >
                    <Import wh={14} color="#374151" />
                    Importer
                  </div>

                  <div className="border-b border-gray-100" />

                  <div
                    className={`flex flex-row gap-2 items-center h-9 p-2 ft-sm ${
                      deletableSelectedPayslips.length > 0
                        ? 'cursor-pointer hover:bg-gray-100 text-red-500'
                        : 'opacity-50 cursor-not-allowed text-red-300'
                    }`}
                    onClick={handleMultipleDelete}
                  >
                    Supprimer
                  </div>
                </div>
              )}
            </div>
          )}

          <Button size="SMI" color="SECONDARY" onClick={handleExportCSV}>
            <span className="text-sm">CSV</span>
          </Button>
        </div>
      </div>

      {isWaiting ? (
        <div className="flex flex-col gap-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : payslips.length === 0 ? (
        <div className="text-sm text-gray-500 text-center py-4">
          Aucune fiche de paie pour cette période
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="px-2 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    className="checkbox-primary cursor-pointer"
                  />
                </th>
                <th className="px-2 py-2">Période</th>
                <th className="px-2 py-2">Montant</th>
                <th className="px-2 py-2">Statut</th>
                <th className="px-2 py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {payslips.map((item) => (
                <PayslipWorkerRow
                  key={`${item.id_temp_worker_bestt}-${item.year}-${item.month}`}
                  item={item}
                  year={year}
                  month={month}
                  idUser={idUser}
                  selectedIds={selectedIds}
                  setSelectedIds={setSelectedIds}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const PayslipWorkerRow = ({
  item,
  year,
  month,
  idUser,
  selectedIds,
  setSelectedIds,
}) => {
  const { toggle } = useModal();
  const {
    setAdminType,
    setAdminTempWorkerId,
    setAdminUserId,
    setAdminFileName,
    setModalData,
  } = UseContext();

  const isSelected = selectedIds.includes(item.id_temp_worker_bestt);
  const isImported = item.status === 'imported';

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const dropDownRef = useRef(null);
  const dotsRef = useRef(null);

  const updateDropdownPos = useCallback(() => {
    if (!dotsRef.current) return;

    const rect = dotsRef.current.getBoundingClientRect();
    const menuHeight = isImported ? 120 : 40;
    const fitsBelow = rect.bottom + 4 + menuHeight < window.innerHeight;

    setDropdownPos({
      top: fitsBelow ? rect.bottom + 4 : rect.top - menuHeight - 4,
      left: rect.right - 176,
    });
  }, [isImported]);

  useEffect(() => {
    const handler = (e) => {
      if (
        dropDownRef.current &&
        !dropDownRef.current.contains(e.target) &&
        dotsRef.current &&
        !dotsRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleCheckbox = () => {
    setSelectedIds((prev) =>
      isSelected
        ? prev.filter((id) => id !== item.id_temp_worker_bestt)
        : [...prev, item.id_temp_worker_bestt]
    );
  };

  const handleViewPayslip = () => {
    setDropdownOpen(false);
    const fileName = `payslip_${item.id_temp_worker_bestt}_${item.year}_${item.month}`;

    setAdminType('payslip');
    setAdminTempWorkerId('');
    setAdminUserId(idUser);
    setAdminFileName(fileName);
    toggle('ad_file_view');
  };

  const handleImport = () => {
    setDropdownOpen(false);
    setModalData({
      payslips: [item],
      year,
      month,
    });
    toggle('payslip_import_unit');
  };

  const handleDelete = () => {
    setDropdownOpen(false);
    setModalData({
      payslips: [item],
      year,
      month,
    });
    toggle('payslip_delete');
  };

  const periodLabel = `${MONTH_NAMES[Number(item.month) - 1] || item.month} ${item.year}`;

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-2 py-2">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={handleCheckbox}
          className="checkbox-primary cursor-pointer"
        />
      </td>

      <td className="px-2 py-2">{periodLabel}</td>

      <td className="px-2 py-2">
        {item.amount ? formatAmount(item.amount) : '-'}
      </td>

      <td className="px-2 py-2">
        <BadgeSquare type={isImported ? 12 : 13} />
      </td>

      <td className="px-2 py-2">
        <div
          className="cursor-pointer w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100"
          ref={dotsRef}
          onClick={() => {
            updateDropdownPos();
            setDropdownOpen((prev) => !prev);
          }}
        >
          <Dots w={14} h={4} color="#374151" />
        </div>

        {dropdownOpen &&
          createPortal(
            <div
              ref={dropDownRef}
              className="fixed border-2 border-[#F3F4F6] rounded-lg shadow-lg w-44 bg-gray-50 z-[9999]"
              style={{ top: dropdownPos.top, left: dropdownPos.left }}
            >
              {!isImported ? (
                <div
                  className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                  onClick={handleImport}
                >
                  Importer
                </div>
              ) : (
                <>
                  <div
                    className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 text-gray-900"
                    onClick={handleViewPayslip}
                  >
                    Consulter la fiche
                  </div>
                  <div
                    className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                    onClick={handleImport}
                  >
                    Importer à nouveau
                  </div>
                  <div
                    className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 text-red-500"
                    onClick={handleDelete}
                  >
                    Supprimer
                  </div>
                </>
              )}
            </div>,
            document.body
          )}
      </td>
    </tr>
  );
};

export default PayslipTempWorker;